import { useRef, useState, useContext, useEffect } from 'react'
import { useRouter } from 'next/router'
import { useFormik } from 'formik'
import * as yup from 'yup'

import { Typography } from 'src/shared/ui/Typography'
import { Popover } from 'src/shared/ui/Popover'
import { Logotype } from 'src/shared/components/Logo'
import { Button } from 'src/shared/ui/Button'
import { Input } from 'src/shared/ui/Input'
import { Modal } from 'src/shared/ui/Modal'
import { ControlIcon, ProfileIcon } from 'src/shared/icons'

import { AuthorizationContext } from 'src/shared/context'

import { useGetDataAuthor } from 'src/shared/api/query'
import { useUpdateAuthor } from 'src/shared/api/mutations'

import styles from './index.module.css'

const formatPhoneNumber = (phone: string) => {
  if (!phone) return phone

  const cleaned = phone.replace(/\D/g, '')
  const match = cleaned.match(/^(\d{1})(\d{3})(\d{3})(\d{2})(\d{2})$/)

  if (match) {
    return `+${match[1]} (${match[2]}) ${match[3]}-${match[4]}-${match[5]}`
  }

  return phone
}

const validationSchema = yup.object({
  lastName: yup.string().required('Обязательное поле'),
  firstName: yup.string().required('Обязательное поле'),
  middleName: yup.string().nullable(),
})

export const Header = () => {
  const router = useRouter()
  const { setToken } = useContext(AuthorizationContext)

  const [open, setOpen] = useState(false)
  const [openModal, setOpenModal] = useState(false)
  const anchorElement = useRef<HTMLDivElement>(null)

  const isAdministrator = router.pathname.startsWith('/admin')
  const isPrivacyPolicy = router.pathname.startsWith('/privacy-policy')

  const { data: author } = useGetDataAuthor({
    enabled: !isAdministrator && !isPrivacyPolicy,
  })
  const { mutateAsync: updateAuthor } = useUpdateAuthor({
    onSuccess: () => {
      setOpenModal(false)
    },
  })

  const formik = useFormik({
    initialValues: {
      lastName: author?.lastName || '',
      firstName: author?.firstName || '',
      middleName: author?.middleName || '',
    },
    validationSchema,
    onSubmit: async (values) => {
      await updateAuthor(values)
    },
    enableReinitialize: true,
  })

  const handleClick = () => {
    setOpen(false)
    setToken('')
    router.push('/')
  }

  useEffect(() => {
    if (openModal) {
      formik.resetForm()
    }
  }, [openModal])

  return (
    <div className={styles.component}>
      <div className={styles.leftContent}>
        <Logotype
          clickable
          onClick={() => router.push(isAdministrator ? '/admin' : isPrivacyPolicy ? '/' : '/profile')}
        />
        {isAdministrator && (
          <div className={styles.links}>
            <Typography view="semibold_14" href="/admin/authors">
              Авторы
            </Typography>
            <Typography view="semibold_14" href="/admin/agreements">
              Согласия
            </Typography>
            <Typography view="semibold_14" href="/admin/products">
              Продукты
            </Typography>
          </div>
        )}
      </div>
      <div className={styles.rightContent}>
        {isAdministrator && (
          <div className={styles.avatar}>
            <ProfileIcon />
          </div>
        )}
        {author && (
          <>
            <div ref={anchorElement} className={styles.phone} onClick={() => setOpen(true)}>
              <Typography view="reg_14">{formatPhoneNumber(author.phone)}</Typography>
              <ControlIcon />
            </div>

            <Popover
              open={open}
              onOpenChange={setOpen}
              anchorElement={anchorElement.current}
              position="bottom-end"
              offset={10}
            >
              <Typography className={styles.action} view="reg_14" onClick={() => setOpenModal(true)}>
                Редактировать
              </Typography>
              <Typography className={styles.action} view="reg_14" onClick={handleClick}>
                Выйти
              </Typography>
            </Popover>
          </>
        )}
      </div>

      <Modal
        open={openModal}
        onOpenChange={setOpenModal}
        className={styles.authorModalWrapper}
        contentProps={{ className: styles.authorModalContent }}
      >
        <Typography view="semibold_20">Редактировать данные</Typography>
        <form onSubmit={formik.handleSubmit} className={styles.authorModalFields}>
          <Input
            name="lastName"
            label="Фамилия"
            labelClassName={styles.authorRequiredField}
            value={formik.values.lastName}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.lastName && formik.errors.lastName}
          />
          <Input
            name="firstName"
            label="Имя"
            labelClassName={styles.authorRequiredField}
            value={formik.values.firstName}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.firstName && formik.errors.firstName}
          />
          <Input
            name="middleName"
            label="Отчество (при наличии)"
            value={formik.values.middleName}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.middleName && formik.errors.middleName}
          />
          <Button type="submit" className={styles.authorModalButton}>
            Сохранить
          </Button>
        </form>
      </Modal>
    </div>
  )
}
